import {
  AppShell,
  ThemeToggle,
  UserMenu,
  useAuth,
  type NavItem,
  type NavSection,
} from "@nexys/user-management-web";
import { Boxes, Building2, LayoutDashboard, MailCheck, ScrollText, User, Users } from "lucide-react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";

/**
 * Authenticated layout. The sidebar navigation uses the kit's
 * `AppShell` with this app's routes wired in via react-router-dom.
 *
 * Items the current user isn't allowed to see (e.g. /users for a
 * non-admin) are filtered out — the routes still exist behind
 * `RequireAuth` so a direct URL is handled by the page-level guards.
 */
export const Shell = () => {
  const { data, hasUserRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (href: string) =>
    href === "/" ? location.pathname === "/" : location.pathname.startsWith(href);

  const item = (key: string, href: string, label: string, icon: NavItem["icon"]): NavItem => ({
    key,
    label,
    icon,
    onClick: () => navigate(href),
    active: isActive(href),
  });

  const overview: NavSection = {
    title: "Overview",
    items: [item("dashboard", "/", "Dashboard", LayoutDashboard)],
  };

  const adminItems: NavItem[] = [];
  if (hasUserRole("superadmin", "useradmin")) {
    adminItems.push(item("users", "/users", "Users", Users));
  }
  if (hasUserRole("superadmin")) {
    adminItems.push(item("tenants", "/tenants", "Tenants", Building2));
    adminItems.push(item("products", "/products", "Products", Boxes));
    adminItems.push(item("logs", "/logs", "Access logs", ScrollText));
    adminItems.push(item("email-test", "/email-test", "Email test", MailCheck));
  }

  const sections: NavSection[] = [overview];
  if (adminItems.length > 0) sections.push({ title: "Admin", items: adminItems });
  sections.push({
    title: "Account",
    items: [item("profile", "/profile", "Profile", User)],
  });

  return (
    <AppShell
      brand={
        <Link to="/" className="hover:opacity-80">
          UM-RS Console
        </Link>
      }
      sections={sections}
      sidebarFooter={data ? <UserMenu /> : null}
      topbar={<ThemeToggle />}
    >
      <Outlet />
    </AppShell>
  );
};
